import './TemplateGalleryModal.scss';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import {
  DashboardTemplateId,
  DashboardTemplateMeta
} from '@blisspointmedia/bpm-types/dist/dashboarding';
import { Spinner } from '@Components/Spinner/Spinner';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { useSetError } from '../../redux/modals';
import { useTemplateTip } from '../../redux/dashboardAgent';
import { useDashboardTemplates, useInstantiateDashboardTemplate } from './hooks/useDashboard';

interface TemplateGalleryModalProps {
  cid: string;
  onClose: () => void;
}

// No preview, no confirmation: picking a template instantiates it immediately and navigates
// straight to the result — the gallery exists to fight blank-paper syndrome, and a confirm step
// works against that.
export const TemplateGalleryModal = ({ cid, onClose }: TemplateGalleryModalProps) => {
  const navigate = useNavigate();
  const setError = useSetError();
  const { data: templates, isLoading, isError } = useDashboardTemplates(cid);
  const { mutateAsync: instantiateTemplate, isPending } = useInstantiateDashboardTemplate(cid);
  const { setTemplateTip } = useTemplateTip();
  // `isPending` alone reaches the DOM only after react-query's notify scheduler flushes -- a
  // macrotask later than the click. That's fine for the disabled visual, but an operation that
  // writes N charts plus a dashboard shouldn't be guarded only by render timing, so this closes
  // the window a fast double-click/double-tap could slip through before the first render lands.
  const pickInFlightRef = useRef(false);

  const handlePick = async (templateId: DashboardTemplateId) => {
    if (pickInFlightRef.current) return;
    pickInFlightRef.current = true;
    try {
      const { dashboard, starterPrompt } = await instantiateTemplate(templateId);
      onClose();
      // Drives the header tip, a fresh agent chat, and the starter prompt offered on tip click.
      setTemplateTip({ dashboardId: dashboard.id, starterPrompt: starterPrompt ?? null });
      navigate(`/${cid}/dashboard/${dashboard.id}`);
    } catch (e) {
      setError({
        message: `Couldn't create a dashboard from this template: ${e.message}`,
        reportError: e
      });
      // Modal stays open on failure so the user can retry -- re-arm the guard for that retry.
      pickInFlightRef.current = false;
    }
  };

  return (
    <Modal show className='template-gallery-modal' onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Start from a template</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <TemplateGalleryBody
          templates={templates}
          isLoading={isLoading}
          isError={isError}
          pickDisabled={isPending}
          onPick={handlePick}
        />
      </Modal.Body>
    </Modal>
  );
};

interface TemplateGalleryBodyProps {
  templates: DashboardTemplateMeta[] | undefined;
  isLoading: boolean;
  isError: boolean;
  pickDisabled: boolean;
  onPick: (templateId: DashboardTemplateId) => void;
}

const TemplateGalleryBody = ({
  templates,
  isLoading,
  isError,
  pickDisabled,
  onPick
}: TemplateGalleryBodyProps) => {
  if (isLoading) return <Spinner />;
  if (isError) {
    return <div className='template-gallery-message'>Couldn&apos;t load templates.</div>;
  }
  if (!templates?.length) {
    return (
      <div className='template-gallery-message'>
        No templates are available for this company yet.
      </div>
    );
  }

  return (
    <div className='template-gallery-list'>
      {templates.map(template => (
        <InteractiveDiv
          key={template.id}
          className='template-gallery-item'
          disabled={pickDisabled}
          onClick={() => onPick(template.id)}
        >
          <span className='template-gallery-item-name'>{template.name}</span>
          {template.description && (
            <span className='template-gallery-item-description'>{template.description}</span>
          )}
        </InteractiveDiv>
      ))}
    </div>
  );
};
